import { Box, Text, Input } from '@chakra-ui/react';
import { useCallback, useRef, useState } from 'react';

const MAX_FILE_SIZE = 1024 * 1024; // 1 MB

interface DropZoneProps {
  accept: string;
  label: string;
  sublabel?: string;
  onFile: (file: File) => void;
  fileName?: string;
  maxSize?: number;
}

const DropZone = ({ accept, label, sublabel, onFile, fileName, maxSize = MAX_FILE_SIZE }: DropZoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sizeError, setSizeError] = useState('');

  const validateAndPass = useCallback(
    (file: File) => {
      if (file.size > maxSize) {
        setSizeError(`File too large (${(file.size / 1024).toFixed(0)} KB). Max ${(maxSize / 1024).toFixed(0)} KB.`);
        return;
      }
      setSizeError('');
      onFile(file);
    },
    [onFile, maxSize],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) validateAndPass(file);
    },
    [validateAndPass],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndPass(file);
  };

  return (
    <Box
      border="2px dashed"
      borderColor={isDragOver ? 'gray.400' : 'gray.200'}
      borderRadius={12}
      p={6}
      textAlign="center"
      cursor="pointer"
      bg={isDragOver ? 'gray.50' : fileName ? 'green.50' : 'white'}
      transition="all 0.15s"
      _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <Input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        display="none"
      />
      {fileName ? (
        <>
          <Text fontSize="sm" fontWeight={600} color="green.600">
            {fileName}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Click or drag to replace
          </Text>
        </>
      ) : (
        <>
          <Text fontSize="sm" fontWeight={500} color="gray.600">
            {label}
          </Text>
          {sublabel && (
            <Text fontSize="xs" color="gray.400" mt={1}>
              {sublabel}
            </Text>
          )}
        </>
      )}
      {sizeError && (
        <Text fontSize="xs" color="red.500" mt={1}>{sizeError}</Text>
      )}
    </Box>
  );
};

export default DropZone;
